import styles from './Dropdown.module.scss';
import { VdsBasicDropdown } from '@volt/volt-extended-components-react';
import type { VdsBasicDropdownCustomEvent } from '@volt/volt-basic-components';
import { Ref, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownItems, { DropdownItemProps } from '../dropdown-items/DropdownItems';
import { getDropdownItemStringValue, getDropdownItemValue } from '../dropdown-items/utils';

interface DropdownProps<T> {
  uid: string;
  // TODO: After upgrading to React 19, replace elementRef with ref since forwardRef is no longer required.
  // https://react.dev/reference/react/forwardRef
  elementRef?: Ref<HTMLVdsBasicDropdownElement>;
  value: T;
  items: DropdownItemProps<T>[];
  onBlur?: () => void;
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  maxHeight?: string; // pixels
  isReadonly?: boolean;
}

export const Dropdown = <T,>({
  uid,
  elementRef,
  value,
  items,
  onBlur,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  maxHeight = '300px',
  isReadonly = false,
}: DropdownProps<T>) => {
  const { t } = useTranslation();

  const onValueChanged = useCallback(
    ({ detail }: VdsBasicDropdownCustomEvent<string | null>) => {
      // Note: 'detail' can be null when the clear button is used or the .clear() method is used.
      if (detail !== null) {
        // look up the value using the dropdown string value
        const value: T | undefined = getDropdownItemValue(items, detail);

        if (typeof value !== 'undefined') {
          onChange(value);
        } else {
          // In theory, this error should not be reached.
          console.error(
            'An event was triggered that contains a vds item value that does not match the item source list.',
          );
        }
      }
    },
    [onChange, items],
  );

  const controlValue: string | undefined = useMemo(
    () => getDropdownItemStringValue(items, value),
    [items, value],
  );

  // If no matching initial value could be found amongst the provided items, return without rendering.
  if (typeof value === 'undefined') {
    return null;
  }

  return (
    <VdsBasicDropdown
      className={styles.dropdown}
      ref={elementRef}
      id={uid}
      /**
       * The VdsBasicDropdown 'value' prop appears to be partially decoupled from state.
       * The 'value' prop acts more like an initial value that gets ignored after the first render.
       * However, if the clear() method (not the clear button) is used to clear the selection, the 'value' prop
       * is then used to determine the selection after the component's internal state has been cleared.
       */
      value={controlValue}
      onValueChanged={onValueChanged}
      onBlur={onBlur}
      placeholder={placeholder ?? t('ShowAll')}
      disabled={disabled}
      error={error}
      /**
       * Disable the clear button, otherwise the user can clear the current value and the component
       * becomes uncontrolled because 'value' is not re-applied.
       */
      showClearButton={false}
      size="medium"
      maxHeight={maxHeight}
      matchWidth
      isReadonly={isReadonly}
    >
      <DropdownItems items={items} />
    </VdsBasicDropdown>
  );
};

export default Dropdown;
